import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { getUtm, http, errMsg } from "@/lib/api";
import { track, trackLead } from "@/lib/track";

export const RegisterForm = ({ config, button, source, onRegistered, idPrefix }) => {
  const [f, setF] = useState({ first_name: "", email: "", marketing_consent: false, website: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const privacyHref = config?.links?.privacy_url || "/privacy";

  const submit = async (e) => {
    e.preventDefault();
    if (!f.first_name.trim()) return setErr("Please tell us your first name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) return setErr("Please enter a valid email address.");
    if (!f.marketing_consent) return setErr("Please tick the box so we can email you about priority access.");
    setErr("");
    setBusy(true);
    try {
      const body = { ...f, email: f.email.trim(), source, ...getUtm() };
      const { data } = await http.post("/signup", body);
      if (!data?.pass) return setErr("Thank you. Please check your inbox.");
      track("sign_up", { source });
      trackLead({ content_name: "Murder Express waitlist" });
      setDone({ name: data.pass.first_name, returning: !!data.returning });
      onRegistered?.(data.pass, body, !!data.returning);
    } catch (x) {
      setErr(errMsg(x));
    } finally {
      setBusy(false);
    }
  };

  if (done) return (
    <div className="font-body w-full max-w-2xl rounded-sm border border-[#C9A66B]/60 bg-[#0D0C09]/70 px-6 py-5 text-center" role="status" data-testid={`${idPrefix}-success`}>
      <p className="text-[12px] font-medium uppercase tracking-[0.2em] text-[#C9A66B]">{done.returning ? "Welcome back" : "You are registered"}</p>
      <p className="mt-2 text-[17px] text-[#F2EBDD]">Thank you, {done.name}. You are on the list. We will email you first with news of the new show and the ticket release.</p>
    </div>
  );

  return (
    <form onSubmit={submit} noValidate className="font-body w-full max-w-2xl" data-testid={`register-form-${source}`}>
      <div className="flex flex-col gap-2 sm:flex-row">
        <label htmlFor={`${idPrefix}-name`} className="sr-only">First name</label>
        <input id={`${idPrefix}-name`} autoComplete="given-name" placeholder="First name" value={f.first_name} onChange={set("first_name")} maxLength={40}
          className="field-wf h-12 rounded-sm px-4 text-[15px] sm:w-40" data-testid={`${idPrefix}-first-name-input`} />
        <label htmlFor={`${idPrefix}-email`} className="sr-only">Email address</label>
        <input id={`${idPrefix}-email`} type="email" inputMode="email" autoComplete="email" placeholder="Your email address" value={f.email} onChange={set("email")}
          className="field-wf h-12 w-full rounded-sm px-4 text-[15px] sm:w-auto sm:flex-1" data-testid={`${idPrefix}-email-input`} />
        <button type="submit" disabled={busy} className="btn-wf inline-flex h-12 items-center justify-center gap-3 rounded-sm px-5 text-[15px] font-medium disabled:opacity-70" data-testid={`${idPrefix}-submit-button`}>
          {busy ? "Registering..." : button}<ArrowRight size={16} aria-hidden="true" />
        </button>
      </div>
      <div className="absolute left-[-9999px] h-px w-px overflow-hidden" aria-hidden="true">
        <input tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} />
      </div>
      <label className="mt-3 flex min-h-[44px] cursor-pointer items-start gap-3 text-[13px] leading-relaxed text-[#F2EBDD]/90">
        <input type="checkbox" className="check check-wf mt-0.5" checked={f.marketing_consent} onChange={set("marketing_consent")} data-testid={`${idPrefix}-consent-checkbox`} />
        <span>{config?.consent_text} <a href={privacyHref} target="_blank" rel="noreferrer" className="underline underline-offset-2">Privacy policy</a>.</span>
      </label>
      {err && <p className="mt-2 text-sm text-[#F2B8A8]" role="alert" data-testid={`${idPrefix}-error`}>{err}</p>}
    </form>
  );
};
